import React, {useEffect, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import {BaseModal} from "../../components/BaseModal";
import {useThemeStore} from "../../stores/themeStore";
import {BarFilter} from "./BarFilter";

import "./FilterSearchModal.scss";

// The BaseModal wrapper stays transparent; the themed card + list live under a
// nested id="ListingsPage" root so every existing sidebar style (bar rows, the
// selected stripe, price/count, the .search-bar input, the light/dark theme
// vars) is reused unchanged. The codebase already reuses this id on other
// portaled surfaces (the map popup, ListingsPageModal).
const wrapperStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    background: 'transparent',
    boxShadow: 'none',
    outline: 'none',
    padding: 0,
};

// A larger, searchable view of a single filter section. It does NOT own any
// selection state: it renders the same BarFilter the sidebar does, wired to the
// same store-backed `isSelected`/`onToggle`, so toggles here stay in sync with
// the sidebar section and the active listings filters, and reopening preserves
// selections. Only the search query is local — it filters the visible options by
// their user-facing label (case-insensitive) and never touches selection state.
export function FilterSearchModal({
    isOpen,
    setIsOpen,
    title,
    items,
    isSelected,
    onToggle,
    getLabel,
    getSecondary,
    renderDot,
}) {
    const theme = useThemeStore((state) => state.theme)
    const [query, setQuery] = useState('')

    // Start each open with a clean search box (selections, which live in the
    // store, are untouched).
    useEffect(() => {
        if (!isOpen) setQuery('')
    }, [isOpen])

    const labelOf = (item) => (getLabel ? getLabel(item) : item.item)
    const q = query.trim().toLowerCase()
    // Preserve the incoming option order; only narrow it when searching.
    const filtered = q
        ? (items || []).filter((it) => String(labelOf(it) ?? '').toLowerCase().includes(q))
        : (items || [])

    return (
        <BaseModal isOpen={isOpen} setIsOpen={setIsOpen} style={wrapperStyle}>
            <div id="ListingsPage" data-theme={theme} className="filter-search-modal-root">
                <div className="filter-search-modal">
                    <div className="fsm-header">
                        <h2 className="fsm-title" id="modal-modal-title">{title}</h2>
                        <button
                            type="button"
                            className="fsm-close"
                            aria-label="Close"
                            onClick={() => setIsOpen(null)}
                        >
                            <CloseIcon/>
                        </button>
                    </div>

                    <div className="search-bar fsm-search">
                        <span className="search-icon"><SearchIcon fontSize="small"/></span>
                        <input
                            className="search-text-input"
                            type="text"
                            /* eslint-disable-next-line jsx-a11y/no-autofocus */
                            autoFocus
                            value={query}
                            placeholder={`Search ${String(title || '').toLowerCase()}`}
                            aria-label={`Search ${title} options`}
                            onChange={(e) => setQuery(e.target.value)}
                        />
                    </div>

                    <div className="fsm-body">
                        {
                            filtered.length > 0
                                ? <BarFilter
                                    items={filtered}
                                    isSelected={isSelected}
                                    onToggle={onToggle}
                                    getLabel={getLabel}
                                    getSecondary={getSecondary}
                                    renderDot={renderDot}
                                />
                                : <div className="fsm-empty">No options match &ldquo;{query.trim()}&rdquo;.</div>
                        }
                    </div>
                </div>
            </div>
        </BaseModal>
    )
}
